import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mic, Sparkles, Square, Volume2, VolumeX, Trash2, Loader2, Hand, Infinity as InfinityIcon } from 'lucide-react';
import { api, API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const VOICE_PRESETS = [
  { id: 'nova',   label: 'Nova',   desc: 'Warm & upbeat' },
  { id: 'shimmer',label: 'Shimmer',desc: 'Bright, cheerful' },
  { id: 'onyx',   label: 'Onyx',   desc: 'Deep & calm' },
  { id: 'fable',  label: 'Fable',  desc: 'Expressive' },
  { id: 'echo',   label: 'Echo',   desc: 'Smooth' },
];

// Silence detection tuning
const VAD_SILENCE_MS = 1500;   // auto-send after this much silence
const VAD_MIN_SPEECH_MS = 600; // require at least this much speech before auto-sending
const VAD_RMS_THRESHOLD = 0.012; // 0-1 RMS below this is "silence"

function pickMime() {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find(c => MediaRecorder.isTypeSupported?.(c)) || '';
}

export function VoicePage() {
  const { user } = useAuth();
  const [turns, setTurns] = useState([]); // {role, text}
  const [recState, setRecState] = useState('idle'); // idle | recording | transcribing | thinking | speaking
  const [elapsed, setElapsed] = useState(0);
  const [voice, setVoice] = useState(() => localStorage.getItem('jixxeo_voice') || 'nova');
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem('jixxeo_voice_autospeak') !== '0');
  const [mode, setMode] = useState(() => localStorage.getItem('jixxeo_voice_mode') || 'hold'); // hold | handsfree
  const [vadLevel, setVadLevel] = useState(0); // 0-1 live mic level for UI
  const [vadStatus, setVadStatus] = useState('idle'); // idle | listening | silence

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null); // conversation_id

  // VAD refs
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const vadStateRef = useRef({ lastVoiceAt: 0, speechStartAt: 0, hasHeardSpeech: false, autoStopped: false });

  useEffect(() => { localStorage.setItem('jixxeo_voice', voice); }, [voice]);
  useEffect(() => { localStorage.setItem('jixxeo_voice_autospeak', autoSpeak ? '1' : '0'); }, [autoSpeak]);
  useEffect(() => { localStorage.setItem('jixxeo_voice_mode', mode); }, [mode]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' }); }, [turns, recState]);

  // Cleanup on unmount
  useEffect(() => () => {
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
    try { audioRef.current?.pause(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    cancelAnimationFrame(vadRafRef.current);
    clearInterval(timerRef.current);
  }, []);

  // ---- VAD (voice activity detection) loop ----
  const startVad = (stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Float32Array(analyser.fftSize);

      vadStateRef.current = { lastVoiceAt: performance.now(), speechStartAt: 0, hasHeardSpeech: false, autoStopped: false };
      setVadStatus('listening');

      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getFloatTimeDomainData(buf);
        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        setVadLevel(Math.min(1, rms * 8));

        const now = performance.now();
        const st = vadStateRef.current;
        if (rms >= VAD_RMS_THRESHOLD) {
          st.lastVoiceAt = now;
          if (!st.hasHeardSpeech) {
            st.speechStartAt = now;
            st.hasHeardSpeech = true;
          }
          setVadStatus('listening');
        } else if (st.hasHeardSpeech) {
          const silenceFor = now - st.lastVoiceAt;
          const speechFor = st.lastVoiceAt - st.speechStartAt;
          if (silenceFor > 300) setVadStatus('silence');
          if (!st.autoStopped && silenceFor >= VAD_SILENCE_MS && speechFor >= VAD_MIN_SPEECH_MS) {
            st.autoStopped = true;
            setVadStatus('idle');
            stopHold(); // triggers mediaRecorder.stop() → handleStop()
            return;
          }
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      vadRafRef.current = requestAnimationFrame(tick);
    } catch {}
  };

  const stopVad = () => {
    cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = null;
    try { analyserRef.current?.disconnect(); } catch {}
    analyserRef.current = null;
    try { audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    setVadLevel(0);
    setVadStatus('idle');
  };

  // ---- Core recording ----
  const startHold = async () => {
    if (!user) { toast.error('Sign in to use Jixxeo Voice'); return; }
    if (recState !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start();
      setRecState('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
      if (mode === 'handsfree') startVad(stream);
    } catch (e) {
      toast.error('Microphone access was blocked. Allow mic in your browser settings.');
      setRecState('idle');
    }
  };

  const stopHold = () => {
    if (recState !== 'recording') return;
    clearInterval(timerRef.current);
    stopVad();
    try { mediaRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks()?.forEach(t => t.stop()); } catch {}
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: mediaRef.current?.mimeType || 'audio/webm' });
    chunksRef.current = [];
    if (blob.size < 2000) {
      toast.error(mode === 'handsfree' ? 'Didn\'t catch that — try speaking again' : 'Hold the button and speak a little longer');
      setRecState('idle');
      return;
    }
    setRecState('transcribing');
    try {
      // 1) Transcribe
      const fd = new FormData();
      const ext = (blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('ogg') ? 'ogg' : 'webm'));
      fd.append('file', new File([blob], `voice.${ext}`, { type: blob.type || 'audio/webm' }));
      const token = localStorage.getItem('jixxeo_token');
      const sttResp = await fetch(`${API}/voice/transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!sttResp.ok) {
        const j = await sttResp.json().catch(() => ({ detail: 'Transcription failed' }));
        throw new Error(j.detail || 'Transcription failed');
      }
      const { text } = await sttResp.json();
      if (!text) { toast.error('Could not understand that — try again'); setRecState('idle'); return; }
      setTurns(t => [...t, { role: 'user', text }]);

      // 2) Chat
      setRecState('thinking');
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const chatResp = await fetch(`${API}/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversation_id: sessionRef.current, text, tool: 'chat', timezone: tz }),
      });
      const reader = chatResp.body.getReader();
      const decoder = new TextDecoder();
      let full = '', convId = sessionRef.current;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data:')) continue;
          try {
            const d = JSON.parse(line.slice(5).trim());
            if (d.conversation_id && !convId) { convId = d.conversation_id; sessionRef.current = convId; }
            if (d.delta) full += d.delta;
          } catch {}
        }
      }
      setTurns(t => [...t, { role: 'assistant', text: full }]);

      // 3) Speak
      if (autoSpeak && full.trim()) {
        setRecState('speaking');
        await speak(full);
      }
      setRecState('idle');

      // 4) Hands-free: auto-restart listening after Jixxeo finishes
      if (mode === 'handsfree') {
        setTimeout(() => { if (recState === 'idle') startHold(); }, 350);
      }
    } catch (e) {
      toast.error(e.message || 'Voice error');
      setRecState('idle');
    }
  };

  const speak = async (text) => {
    try {
      const token = localStorage.getItem('jixxeo_token');
      const r = await fetch(`${API}/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, voice }),
      });
      if (!r.ok) throw new Error('tts failed');
      const buf = await r.blob();
      const url = URL.createObjectURL(buf);
      const audio = new Audio(url);
      audioRef.current = audio;
      await new Promise((resolve) => {
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        audio.play().catch(() => resolve());
      });
    } catch { toast.error('Could not play voice'); }
  };

  const stopSpeaking = () => {
    try { audioRef.current?.pause(); audioRef.current = null; } catch {}
    if (recState === 'speaking') setRecState('idle');
  };

  const clearChat = () => {
    stopSpeaking();
    if (recState === 'recording') stopHold();
    setTurns([]);
    sessionRef.current = null;
    toast.success('Conversation cleared');
  };

  const tapMic = () => {
    if (mode === 'handsfree') {
      if (recState === 'recording') { stopHold(); } else if (recState === 'idle') { startHold(); }
    }
  };

  const stateText = (() => {
    if (recState === 'recording') {
      if (mode === 'handsfree') {
        if (vadStatus === 'silence') return `Hearing silence… I'll send when you pause ${elapsed}s`;
        return `Listening… speak freely ${elapsed}s`;
      }
      return `Listening… ${elapsed}s`;
    }
    return {
      idle: mode === 'handsfree' ? 'Tap to start — Jixxeo auto-sends on pause' : 'Hold to talk',
      transcribing: 'Transcribing…',
      thinking: 'Jixxeo is thinking…',
      speaking: 'Jixxeo is speaking…',
    }[recState];
  })();

  const busy = recState !== 'idle';
  const pulsing = recState === 'recording';

  // Hands-free props vs push-to-talk props on the mic button
  const micHandlers = mode === 'handsfree'
    ? { onClick: tapMic }
    : {
        onPointerDown: startHold,
        onPointerUp: stopHold,
        onPointerLeave: () => recState === 'recording' && stopHold(),
        onPointerCancel: () => recState === 'recording' && stopHold(),
      };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="text-center">
        <h1 className="font-display text-2xl sm:text-3xl font-black">Jixxeo <span className="gradient-text">Voice</span></h1>
        <p className="text-slate-400 text-sm mt-1">
          {mode === 'handsfree' ? 'Hands-free: speak, pause, and Jixxeo takes over.' : 'Hold the button, speak, and Jixxeo talks back.'}
        </p>
      </div>

      {/* Mode toggle */}
      <div data-testid="voice-mode-toggle" className="flex items-center justify-center gap-2 p-1 rounded-full bg-white/5 border border-white/10 w-fit mx-auto">
        <button
          data-testid="voice-mode-hold"
          onClick={() => { if (recState === 'recording') stopHold(); setMode('hold'); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
            ${mode === 'hold' ? 'gradient-brand text-white shadow' : 'text-slate-400 hover:text-white'}`}
        >
          <Hand className="w-3.5 h-3.5" /> Push to talk
        </button>
        <button
          data-testid="voice-mode-handsfree"
          onClick={() => { if (recState === 'recording') stopHold(); setMode('handsfree'); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
            ${mode === 'handsfree' ? 'gradient-brand text-white shadow' : 'text-slate-400 hover:text-white'}`}
        >
          <InfinityIcon className="w-3.5 h-3.5" /> Hands-free
        </button>
      </div>

      {/* Conversation */}
      <Card className="glass p-3 sm:p-4">
        <div ref={scrollRef} className="h-[36vh] overflow-y-auto space-y-3 pr-1">
          {turns.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-2">
              <Sparkles className="w-6 h-6 text-violet-400" />
              Try: "What's on my calendar today?" or "Tell me a joke."
            </div>
          )}
          {turns.map((m, i) => (
            <div key={i} data-testid={`voice-turn-${m.role}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap
                ${m.role === 'user' ? 'gradient-brand text-white rounded-br-sm' : 'bg-white/5 border border-white/10 rounded-bl-sm'}`}>
                {m.text}
              </div>
            </div>
          ))}
          {(recState === 'transcribing' || recState === 'thinking' || recState === 'speaking') && (
            <div className="flex justify-start">
              <div className="px-3.5 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-xs text-violet-200 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {stateText}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Big mic button */}
      <div className="flex flex-col items-center gap-3 py-1">
        <button
          data-testid="voice-mic-btn"
          disabled={busy && recState !== 'recording'}
          className={`relative w-32 h-32 rounded-full flex items-center justify-center shadow-2xl shadow-violet-500/40 select-none
            transition-transform duration-200 active:scale-95
            ${pulsing ? 'gradient-gold text-slate-950' : 'gradient-brand text-white'}
            ${busy && recState !== 'recording' ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
          style={{ touchAction: 'none' }}
          aria-label={pulsing ? (mode === 'handsfree' ? 'Tap to stop' : 'Release to send') : (mode === 'handsfree' ? 'Tap to start' : 'Hold to talk')}
          {...micHandlers}
        >
          {pulsing ? <Square className="w-12 h-12" fill="currentColor" /> : <Mic className="w-14 h-14" />}
          {pulsing && (
            <>
              {/* Live level ring (hands-free mode) */}
              {mode === 'handsfree' && (
                <span
                  data-testid="voice-level-ring"
                  className="absolute -inset-1 rounded-full border-4 border-amber-300/70 transition-transform duration-100"
                  style={{ transform: `scale(${1 + vadLevel * 0.35})`, opacity: 0.3 + vadLevel * 0.6 }}
                />
              )}
              <span className="absolute inset-0 rounded-full border-4 border-amber-300/50 animate-ping" />
              <span className="absolute -inset-2 rounded-full border-2 border-amber-400/30 animate-pulse" />
            </>
          )}
        </button>
        <div data-testid="voice-state" className="text-xs sm:text-sm font-semibold text-slate-200 min-h-[1.25rem] text-center px-4">
          {stateText}
        </div>
      </div>

      {/* Controls */}
      <Card className="glass p-4 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Voice</div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
            {VOICE_PRESETS.map(v => (
              <button
                key={v.id}
                data-testid={`voice-preset-${v.id}`}
                onClick={() => setVoice(v.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                  ${voice === v.id ? 'border-violet-400 bg-violet-500/20 text-violet-100' : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'}`}
              >
                <span className="font-bold">{v.label}</span> <span className="text-slate-500">· {v.desc}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/5 flex-wrap">
          <button
            data-testid="voice-autospeak-toggle"
            onClick={() => setAutoSpeak(a => !a)}
            className="flex items-center gap-2 text-xs text-slate-300 hover:text-white"
          >
            {autoSpeak ? <Volume2 className="w-4 h-4 text-violet-300" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
            Auto-speak replies: <span className={autoSpeak ? 'text-violet-300 font-bold' : 'text-slate-500'}>{autoSpeak ? 'ON' : 'OFF'}</span>
          </button>
          <div className="flex gap-2">
            {recState === 'speaking' && (
              <Button data-testid="voice-stop-speak" size="sm" variant="outline" onClick={stopSpeaking} className="border-white/20">
                <Square className="w-3 h-3 mr-1" /> Stop
              </Button>
            )}
            <Button data-testid="voice-clear" size="sm" variant="outline" onClick={clearChat} className="border-white/20" disabled={turns.length === 0 && recState === 'idle'}>
              <Trash2 className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function AIHub() {
  const tools = [
    { to: '/ai/chat', label: 'AI Chat', desc: 'Ask anything' },
    { to: '/ai/image', label: 'Image Studio', desc: 'Generate images' },
    { to: '/ai/video', label: 'Video Studio', desc: 'AI videos' },
    { to: '/ai/writing', label: 'Writing', desc: 'Emails, blogs, stories' },
    { to: '/ai/code', label: 'Code', desc: 'Generate & debug' },
    { to: '/ai/homework', label: 'Homework', desc: 'Study help' },
    { to: '/ai/research', label: 'Research', desc: 'Deep answers' },
    { to: '/ai/translate', label: 'Translate', desc: 'Any language' },
    { to: '/ai/voice', label: 'Voice', desc: 'Talk to AI' },
    { to: '/leaderboards', label: 'Leaderboards', desc: 'Top creators & streaks' },
    { to: '/history', label: 'History', desc: 'All your saved chats' },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Sparkles className="w-7 h-7 text-violet-400" /> AI Workspace</h1>
        <p className="text-sm text-slate-400">All your Jixxeo AI tools in one place</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {tools.map(t => (
          <a key={t.to} href={t.to} data-testid={`hub-${t.label.toLowerCase().replace(/\s+/g,'-')}`} className="glass rounded-2xl p-5 hover:border-violet-500/60 transition-colors">
            <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center mb-3"><Sparkles className="w-5 h-5 text-white" /></div>
            <div className="font-display font-bold">{t.label}</div>
            <div className="text-xs text-slate-400 mt-1">{t.desc}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

export function CreateHub() {
  const opts = [
    { to: '/ai/image', label: 'Generate Image', desc: 'Create art with AI' },
    { to: '/ai/video', label: 'Generate Video', desc: 'AI video clips' },
    { to: '/ai/writing', label: 'Write content', desc: 'Posts, blogs, stories' },
    { to: '/marketplace', label: 'Sell on marketplace', desc: 'List prompts & templates' },
    { to: '/subscriptions', label: 'Upgrade to Premium', desc: 'Unlock all Jixxeo AI power' },
  ];
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold">Create</h1>
        <p className="text-sm text-slate-400">What will you make today?</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {opts.map(o => (
          <a key={o.to} href={o.to} data-testid={`create-${o.label.toLowerCase().replace(/\s+/g,'-')}`} className="glass rounded-2xl p-6 hover:border-violet-500/60 transition-colors">
            <div className="font-display text-lg font-bold">{o.label}</div>
            <div className="text-sm text-slate-400 mt-1">{o.desc}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

